import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { CircularTimer, CircularProgress } from '../ui/circular-timer';

const FileUpload = ({ 
  onUpload, 
  onComplete,
  accept = '.pdf,.docx,.doc,.txt,.rtf',
  maxFiles = 20,
  title = 'Upload File',
  description = 'Drag and drop your files here, or click to browse',
  showProviderSelect = true,
}) => {
  const [files, setFiles] = useState([]);
  const [aiProvider, setAiProvider] = useState('anthropic');  // Default to Anthropic/Claude
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      status: 'pending', // pending, uploading, queued, error
    }));
    setFiles(prev => [...prev, ...newFiles].slice(0, maxFiles));
  }, [maxFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/msword': ['.doc'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'text/rtf': ['.rtf'],
    },
    maxFiles,
    multiple: true,
  });

  const removeFile = (fileId) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    let queuedCount = 0;
    let errorCount = 0;
    
    // Upload all files quickly (they'll be processed in background)
    for (let i = 0; i < files.length; i++) {
      const fileItem = files[i];
      if (fileItem.status === 'queued') {
        queuedCount++;
        continue;
      }
      
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'uploading' } : f
      ));
      setUploadProgress({ current: i + 1, total: files.length });
      
      try {
        await onUpload(fileItem.file, aiProvider);
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'queued' } : f
        ));
        queuedCount++;
      } catch (error) {
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'error' } : f
        ));
        errorCount++;
      }
    }
    
    setUploading(false);
    
    // If all files queued successfully, close dialog and notify
    if (queuedCount > 0 && onComplete) {
      setTimeout(() => {
        onComplete(queuedCount, errorCount);
      }, 500);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      {showProviderSelect && (
        <div className="space-y-2">
          <Label>AI Provider for Parsing</Label>
          <Select value={aiProvider} onValueChange={setAiProvider}>
            <SelectTrigger className="w-full md:w-64" data-testid="ai-provider-select">
              <SelectValue placeholder="Select AI Provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="anthropic">Anthropic Claude</SelectItem>
              <SelectItem value="openai">OpenAI GPT</SelectItem>
              <SelectItem value="gemini">Google Gemini</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            Select the AI provider to parse the uploaded documents
          </p>
        </div>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'upload-zone',
          isDragActive && 'dragging'
        )}
        data-testid="file-dropzone"
      >
        <input {...getInputProps()} data-testid="file-input" />
        <Upload className="w-10 h-10 mx-auto text-slate-400 mb-4" />
        <p className="text-sm font-medium text-slate-700">{title}</p>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
        <p className="text-xs text-slate-400 mt-2">
          Supported: PDF, DOCX, DOC, TXT, RTF (up to {maxFiles} files)
        </p>
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <div className="max-h-48 overflow-y-auto space-y-2 scrollbar-thin">
            {files.map((fileItem) => (
              <div
                key={fileItem.id}
                className={cn(
                  "flex items-center gap-3 p-3 bg-white border rounded-lg",
                  fileItem.status === 'error' ? 'border-red-200 bg-red-50' : 'border-slate-200'
                )}
              >
                <FileText className="w-5 h-5 text-slate-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 truncate">{fileItem.name}</p>
                  <p className="text-xs text-slate-500">
                    {formatFileSize(fileItem.size)}
                    {fileItem.status === 'queued' && ' • Queued for processing'}
                    {fileItem.status === 'error' && ' • Failed to upload'}
                  </p>
                </div>
                
                {fileItem.status === 'uploading' && (
                  <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                )}
                {fileItem.status === 'queued' && (
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                )}
                {fileItem.status === 'error' && (
                  <AlertCircle className="w-5 h-5 text-red-500" />
                )}
                
                {fileItem.status === 'pending' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(fileItem.id); }}
                    className="p-1 text-slate-400 hover:text-slate-600"
                    data-testid={`remove-file-${fileItem.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {uploading && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-center gap-6">
                <CircularTimer 
                  duration={files.length * 8}
                  size={80}
                  strokeWidth={6}
                  isRunning={uploading}
                  label={`${uploadProgress.current}/${uploadProgress.total} files`}
                />
                <div className="text-left">
                  <p className="text-sm font-medium text-blue-700">
                    Uploading & Processing...
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    File {uploadProgress.current} of {uploadProgress.total}
                  </p>
                  <div className="mt-2 w-32 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-blue-500 transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <Button
            onClick={handleUpload}
            disabled={uploading || files.every(f => f.status === 'queued')}
            className="w-full mt-4"
            data-testid="upload-submit-btn"
          >
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : files.some(f => f.status === 'queued') ? (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                {files.filter(f => f.status === 'queued').length} Files Queued
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {files.length} File{files.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
          
          <p className="text-xs text-center text-slate-500">
            Files will be parsed in the background. Check the bell icon for updates.
          </p>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
